import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Divider, IconButton, InputAdornment, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { Add, CheckBox, CheckBoxOutlineBlank, Delete } from "@mui/icons-material";
import { Button, ChoiceToggle, FieldWrapper, OptionCardGroup, PageHeader, StepActions } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { STATUS_CATALOG } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";
import { usePostValidateNextStageMutation } from "@api/applications";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import * as Sentry from "@sentry/react";

const SECTOR_OPTIONS = [
  { value: "agricultura", label: "Agricultura" },
  { value: "ganaderia", label: "Ganadería" },
  { value: "comercio", label: "Comercio" },
  { value: "servicios", label: "Servicios" },
  { value: "otros", label: "Otros" },
];

const CULTIVOS_CATALOG = [
  "Alfalfa establecimiento",
  "Alfalfa mantenimiento",
  "Avena para semilla",
  "Cebada grano",
  "Frijol",
  "Maíz amarillo grano",
  "Maíz blanco grano",
  "Sorgo grano",
  "Trigo cristalino",
  "Trigo panificable",
  "Otro cultivo",
];

const ESPECIES_CATALOG = ["Bovino carne", "Bovino leche", "Porcino", "Ovino o caprino", "Avícola", "Otra especie"];

const CON_QUE_OPTIONS = [
  { value: "tierra_propia", label: "Tierra propia" },
  { value: "tierra_rentada", label: "Tierra rentada" },
  { value: "corrales", label: "Corrales o establos propios" },
  { value: "bodega_propia", label: "Bodega propia" },
  { value: "bodega_rentada", label: "Bodega rentada" },
  { value: "maq_propia", label: "Maquinaria propia" },
  { value: "maq_rentada", label: "Maquinaria rentada" },
  { value: "pozo_riego", label: "Pozo o sistema de riego" },
  { value: "transporte", label: "Transporte propio" },
];

interface Contraparte {
  id: string;
  nombre: string;
  porcentaje: number | "";
}

interface ConQuienData {
  clientes: Contraparte[];
  proveedores: Contraparte[];
}

interface Cultivo {
  id: string;
  cultivo: string;
  ha: number | "";
  seFinancia: boolean;
}

interface QueSiembrasData {
  cultivos: Cultivo[];
  tipoProduccion: string;
  ciclosPorAno: string;
}

interface Hato {
  id: string;
  especie: string;
  cabezas: number | "";
  seFinancia: boolean;
}

const newContraparte = (): Contraparte => ({
  id: Math.random().toString(36).slice(2),
  nombre: "",
  porcentaje: "",
});

const newCultivo = (): Cultivo => ({
  id: Math.random().toString(36).slice(2),
  cultivo: "",
  ha: "",
  seFinancia: false,
});

const newHato = (): Hato => ({
  id: Math.random().toString(36).slice(2),
  especie: "",
  cabezas: "",
  seFinancia: false,
});

const SectionBadge = ({ n }: { n: number }) => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: "6px",
      bgcolor: "primary.accent",
      color: "#fff",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12,
      fontWeight: 700,
      flexShrink: 0,
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{n}</Typography>
  </Box>
);

const QueSiembras = ({ value, onChange }: { value: QueSiembrasData; onChange: (v: QueSiembrasData) => void }) => {
  const set = (patch: Partial<QueSiembrasData>) => onChange({ ...value, ...patch });

  const updateCultivo = (id: string, patch: Partial<Cultivo>) =>
    set({ cultivos: value.cultivos.map((c) => (c.id === id ? { ...c, ...patch } : c)) });

  const removeCultivo = (id: string) => set({ cultivos: value.cultivos.filter((c) => c.id !== id) });

  const totalHa = value.cultivos.reduce((sum, c) => sum + (Number(c.ha) || 0), 0);
  const financiadoHa = value.cultivos.filter((c) => c.seFinancia).reduce((sum, c) => sum + (Number(c.ha) || 0), 0);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "6px" }}>
        <SectionBadge n={2} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark" }}>
          Qué siembras
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 13, color: "tokens.textTertiary", mb: "14px", lineHeight: 1.5 }}>
        Un renglón por cultivo, con sus hectáreas. Marca los que vas a financiar con este crédito.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {value.cultivos.map((c) => (
          <Box key={c.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              select
              size="lendia_input"
              value={c.cultivo}
              onChange={(e) => updateCultivo(c.id, { cultivo: e.target.value })}
              sx={{ flex: 1 }}
              SelectProps={{ displayEmpty: true }}
              inputProps={{ "data-testid": `perfil-cultivo-select-${c.id}` }}
            >
              <MenuItem value="" disabled>
                Cultivo
              </MenuItem>
              {CULTIVOS_CATALOG.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="lendia_input"
              type="number"
              value={c.ha}
              onChange={(e) => updateCultivo(c.id, { ha: e.target.value === "" ? "" : Number(e.target.value) })}
              sx={{ width: 90 }}
              inputProps={{ min: 0, "data-testid": `perfil-cultivo-ha-${c.id}` }}
              placeholder="Ha"
            />

            <Button
              tone={c.seFinancia ? "primary" : "outline"}
              size="small"
              startIcon={c.seFinancia ? <CheckBox fontSize="small" /> : <CheckBoxOutlineBlank fontSize="small" />}
              onClick={() => updateCultivo(c.id, { seFinancia: !c.seFinancia })}
              sx={{ textTransform: "none", whiteSpace: "nowrap", height: 42, borderRadius: 2 }}
            >
              Se financia
            </Button>

            <IconButton
              size="small"
              onClick={() => removeCultivo(c.id)}
              disabled={value.cultivos.length === 1}
              sx={{ color: "tokens.textTertiary" }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "10px" }}>
        <Button
          tone="ghost"
          startIcon={<Add />}
          size="small"
          onClick={() => set({ cultivos: [...value.cultivos, newCultivo()] })}
          sx={{ textTransform: "none", fontWeight: 600, fontSize: 13 }}
        >
          Agregar cultivo
        </Button>
        {totalHa > 0 && (
          <Typography sx={{ fontSize: 12, color: "tokens.textTertiary" }}>
            {totalHa} ha en total{financiadoHa > 0 && ` · ${financiadoHa} ha en este crédito`}
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: 2, display: "flex", gap: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "primary.dark", mb: "8px" }}>
            Tipo de producción
          </Typography>
          <ChoiceToggle
            value={value.tipoProduccion}
            onChange={(v) => set({ tipoProduccion: String(v) })}
            options={[
              { value: "temporal", label: "Temporal" },
              { value: "riego", label: "Riego" },
              { value: "mixta", label: "Mixta" },
            ]}
            size="small"
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "primary.dark", mb: "8px" }}>
            Ciclos por año
          </Typography>
          <ChoiceToggle
            value={value.ciclosPorAno}
            onChange={(v) => set({ ciclosPorAno: String(v) })}
            options={[
              { value: "1", label: "1" },
              { value: "2", label: "2" },
              { value: "3+", label: "3 o más" },
            ]}
            size="small"
          />
        </Box>
      </Box>
    </Box>
  );
};

const QueGanado = ({ value, onChange, n }: { value: Hato[]; onChange: (v: Hato[]) => void; n: number }) => {
  const updateHato = (id: string, patch: Partial<Hato>) =>
    onChange(value.map((h) => (h.id === id ? { ...h, ...patch } : h)));

  const removeHato = (id: string) => onChange(value.filter((h) => h.id !== id));

  const totalCab = value.reduce((sum, h) => sum + (Number(h.cabezas) || 0), 0);
  const financiadoCab = value.filter((h) => h.seFinancia).reduce((sum, h) => sum + (Number(h.cabezas) || 0), 0);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "6px" }}>
        <SectionBadge n={n} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark" }}>
          Qué ganado tienes
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 13, color: "tokens.textTertiary", mb: "14px", lineHeight: 1.5 }}>
        Un renglón por especie, con sus cabezas. Marca lo que entra en este crédito.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {value.map((h) => (
          <Box key={h.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              select
              size="lendia_input"
              value={h.especie}
              onChange={(e) => updateHato(h.id, { especie: e.target.value })}
              sx={{ flex: 1 }}
              SelectProps={{ displayEmpty: true }}
              inputProps={{ "data-testid": `perfil-especie-select-${h.id}` }}
            >
              <MenuItem value="" disabled>
                Especie
              </MenuItem>
              {ESPECIES_CATALOG.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="lendia_input"
              type="number"
              value={h.cabezas}
              onChange={(e) => updateHato(h.id, { cabezas: e.target.value === "" ? "" : Number(e.target.value) })}
              sx={{ width: 110 }}
              inputProps={{ min: 0, "data-testid": `perfil-especie-cabezas-${h.id}` }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography sx={{ fontSize: 13, color: "tokens.textTertiary" }}>cab</Typography>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              tone={h.seFinancia ? "primary" : "outline"}
              size="small"
              startIcon={h.seFinancia ? <CheckBox fontSize="small" /> : <CheckBoxOutlineBlank fontSize="small" />}
              onClick={() => updateHato(h.id, { seFinancia: !h.seFinancia })}
              sx={{ whiteSpace: "nowrap", minWidth: 130, height: 42, textTransform: "none", borderRadius: "8px" }}
            >
              Se financia
            </Button>

            <IconButton
              size="small"
              onClick={() => removeHato(h.id)}
              disabled={value.length === 1}
              sx={{ color: "tokens.textTertiary" }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "10px" }}>
        <Button
          tone="ghost"
          startIcon={<Add />}
          size="small"
          onClick={() => onChange([...value, newHato()])}
          sx={{ textTransform: "none", fontWeight: 600, fontSize: 13 }}
        >
          Agregar especie
        </Button>
        {totalCab > 0 && (
          <Typography sx={{ fontSize: 12, color: "tokens.textTertiary" }}>
            {totalCab} cabezas en total{financiadoCab > 0 && ` · ${financiadoCab} en este crédito`}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ContraparteRow = ({
  row,
  placeholder,
  onUpdate,
  onRemove,
  removable,
}: {
  row: Contraparte;
  placeholder: string;
  onUpdate: (patch: Partial<Contraparte>) => void;
  onRemove: () => void;
  removable: boolean;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <TextField
      size="lendia_input"
      value={row.nombre}
      onChange={(e) => onUpdate({ nombre: e.target.value })}
      placeholder={placeholder}
      sx={{ flex: 1 }}
      inputProps={{ "data-testid": `perfil-contraparte-nombre-${row.id}` }}
    />
    <TextField
      size="lendia_input"
      type="number"
      value={row.porcentaje}
      onChange={(e) => onUpdate({ porcentaje: e.target.value === "" ? "" : Number(e.target.value) })}
      placeholder="0"
      sx={{ width: 90 }}
      inputProps={{ min: 0, max: 100, "data-testid": `perfil-contraparte-pct-${row.id}` }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Typography sx={{ fontSize: 13, color: "tokens.textTertiary" }}>%</Typography>
          </InputAdornment>
        ),
      }}
    />
    <IconButton size="small" onClick={onRemove} disabled={!removable} sx={{ color: "tokens.textTertiary" }}>
      <Delete fontSize="small" />
    </IconButton>
  </Box>
);

const ConQuienOperas = ({
  value,
  onChange,
  n,
}: {
  value: ConQuienData;
  onChange: (v: ConQuienData) => void;
  n: number;
}) => {
  const updateRow = (key: "clientes" | "proveedores", id: string, patch: Partial<Contraparte>) =>
    onChange({ ...value, [key]: value[key].map((r) => (r.id === id ? { ...r, ...patch } : r)) });

  const removeRow = (key: "clientes" | "proveedores", id: string) =>
    onChange({ ...value, [key]: value[key].filter((r) => r.id !== id) });

  const addRow = (key: "clientes" | "proveedores") => {
    if (value[key].length >= 4) return;
    onChange({ ...value, [key]: [...value[key], newContraparte()] });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "6px" }}>
        <SectionBadge n={n} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark" }}>
          Con quién operas
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 13, color: "tokens.textTertiary", mb: "16px", lineHeight: 1.5 }}>
        Tus principales clientes y proveedores, con el porcentaje aproximado que representan. Incluye también a los que
        no te facturan.
      </Typography>

      <FieldWrapper label="Principales clientes" hint="A quién le vendes y qué parte de tus ventas es." error="">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {value.clientes.map((r) => (
            <ContraparteRow
              key={r.id}
              row={r}
              placeholder="Nombre del cliente"
              onUpdate={(patch) => updateRow("clientes", r.id, patch)}
              onRemove={() => removeRow("clientes", r.id)}
              removable={value.clientes.length > 1}
            />
          ))}
          {value.clientes.length < 4 && (
            <Box>
              <Button
                tone="ghost"
                startIcon={<Add />}
                size="small"
                onClick={() => addRow("clientes")}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: 13 }}
              >
                Agregar cliente
              </Button>
            </Box>
          )}
        </Box>
      </FieldWrapper>

      <Box sx={{ mt: 2 }}>
        <FieldWrapper label="Principales proveedores" hint="A quién le compras y qué parte de tus compras es." error="">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {value.proveedores.map((r) => (
              <ContraparteRow
                key={r.id}
                row={r}
                placeholder="Nombre del proveedor"
                onUpdate={(patch) => updateRow("proveedores", r.id, patch)}
                onRemove={() => removeRow("proveedores", r.id)}
                removable={value.proveedores.length > 1}
              />
            ))}
            {value.proveedores.length < 4 && (
              <Box>
                <Button
                  tone="ghost"
                  startIcon={<Add />}
                  size="small"
                  onClick={() => addRow("proveedores")}
                  sx={{ textTransform: "none", fontWeight: 600, fontSize: 13 }}
                >
                  Agregar proveedor
                </Button>
              </Box>
            )}
          </Box>
        </FieldWrapper>
      </Box>
    </Box>
  );
};

const ConQueOperas = ({
  value,
  onChange,
  hectareasRentadas,
  onHectareasChange,
  n,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  hectareasRentadas: number | "";
  onHectareasChange: (v: number | "") => void;
  n: number;
}) => (
  <Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "6px" }}>
      <SectionBadge n={n} />
      <Typography sx={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark" }}>
        Con qué operas
      </Typography>
    </Box>
    <Typography sx={{ fontSize: 13, color: "tokens.textTertiary", mb: "14px", lineHeight: 1.5 }}>
      Nos dice qué respalda tu operación. No pedimos activo por activo ni valores: eso es expediente.
    </Typography>
    <OptionCardGroup layout="check" value={value} onChange={onChange} options={CON_QUE_OPTIONS} columns={3} />
    {value.includes("tierra_rentada") && (
      <Box sx={{ mt: 2, maxWidth: 300 }}>
        <FieldWrapper label="Hectáreas que rentas" error="">
          <TextField
            size="lendia_input"
            type="number"
            fullWidth
            value={hectareasRentadas}
            onChange={(e) => onHectareasChange(e.target.value === "" ? "" : Number(e.target.value))}
            placeholder="0"
            inputProps={{ min: 0, "data-testid": "perfil-hectareas-rentadas-input" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography sx={{ fontSize: 13, color: "tokens.textTertiary" }}>ha</Typography>
                </InputAdornment>
              ),
            }}
          />
        </FieldWrapper>
      </Box>
    )}
  </Box>
);

export const PerfilOperativo = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { nextStatus, previousStatus } = useStatusConfig(STATUS_CATALOG.perfil_operativo.code);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const [triggerPostNextStage, { isLoading: isSubmitting }] = usePostValidateNextStageMutation();

  const [sectors, setSectors] = useState<string[]>([]);
  const [actividadPrincipal, setActividadPrincipal] = useState("");
  const [otroGiroTxt, setOtroGiroTxt] = useState("");
  const [siembras, setSiembras] = useState<QueSiembrasData>({
    cultivos: [newCultivo()],
    tipoProduccion: "",
    ciclosPorAno: "",
  });
  const [ganado, setGanado] = useState<Hato[]>([newHato()]);
  const [conQuien, setConQuien] = useState<ConQuienData>({
    clientes: [newContraparte()],
    proveedores: [newContraparte()],
  });
  const [conQue, setConQue] = useState<string[]>([]);
  const [hectareasRentadas, setHectareasRentadas] = useState<number | "">("");

  const tieneAgri = sectors.includes("agricultura");
  const tieneGan = sectors.includes("ganaderia");
  const tieneOtro = sectors.some((s) => ["comercio", "servicios", "otros"].includes(s));
  const showPrincipal = sectors.length > 1;
  const principalOptions = SECTOR_OPTIONS.filter((o) => sectors.includes(o.value));

  const nConQuien = 2 + (tieneAgri ? 1 : 0) + (tieneGan ? 1 : 0);
  const nConQue = nConQuien + 1;

  const handleSectorsChange = (newSectors: string[]) => {
    setSectors(newSectors);
    if (actividadPrincipal && !newSectors.includes(actividadPrincipal)) setActividadPrincipal("");
    if (!newSectors.some((s) => ["comercio", "servicios", "otros"].includes(s))) setOtroGiroTxt("");
  };

  const handleNext = async () => {
    try {
      if (!nextStatus) throw new Error("No se encontró el siguiente status");
      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();
      navigate(nextStatus.url);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "PerfilOperativo-handleNext" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    }
  };

  const siembrasOk =
    !tieneAgri ||
    (siembras.cultivos.some((c) => c.cultivo !== "" && c.ha !== "") &&
      siembras.tipoProduccion !== "" &&
      siembras.ciclosPorAno !== "");
  const ganadoOk = !tieneGan || ganado.some((h) => h.especie !== "" && h.cabezas !== "");
  const clientesOk = conQuien.clientes.some((c) => c.nombre.trim() !== "");
  const conQueOk = conQue.length > 0;

  const canContinue =
    sectors.length > 0 &&
    (!showPrincipal || actividadPrincipal !== "") &&
    siembrasOk &&
    ganadoOk &&
    clientesOk &&
    conQueOk;

  return (
    <Box>
      <PageHeader
        eyebrow="PERFIL OPERATIVO"
        title="Lo que tus facturas no nos dicen"
        subtitle="Ya leímos tu constancia de situación fiscal y tus CFDI de los últimos 12 meses. Faltan las cosas que solo tú sabes: qué siembras o criás, cuánto de eso vas a financiar, con quién operas y con qué."
      />

      <Paper variant="lendia_paper">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "6px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SectionBadge n={1} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark" }}>
              A qué te dedicas
            </Typography>
          </Box>
          {sectors.length > 1 && (
            <Typography sx={{ fontSize: 12, color: "tokens.textTertiary" }}>{sectors.length} giros</Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: 13, color: "tokens.textTertiary", mb: "14px", lineHeight: 1.5 }}>
          Tu constancia registra una sola actividad. Si haces varias, márcalas todas: el análisis necesita el negocio
          completo, no el del RFC.
        </Typography>

        <OptionCardGroup
          layout="check"
          value={sectors}
          onChange={handleSectorsChange}
          options={SECTOR_OPTIONS}
          columns={5}
        />

        {showPrincipal && (
          <Box sx={{ mt: 2, maxWidth: 420 }}>
            <FieldWrapper label="¿Cuál es tu actividad principal?" hint="La que más ingresos te deja." error="">
              <TextField
                select
                size="lendia_input"
                fullWidth
                value={actividadPrincipal}
                onChange={(e) => setActividadPrincipal(e.target.value)}
                inputProps={{ "data-testid": "perfil-actividad-principal-select" }}
              >
                {principalOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </FieldWrapper>
          </Box>
        )}

        {tieneOtro && (
          <Box sx={{ mt: 2 }}>
            <FieldWrapper
              label={sectors.includes("otros") ? "Especifica tu actividad" : "¿Qué comercializas o qué servicio das?"}
              error=""
            >
              <TextField
                size="lendia_input"
                fullWidth
                placeholder="Ej. Compra-venta de granos y flete a productores de la zona."
                value={otroGiroTxt}
                onChange={(e) => setOtroGiroTxt(e.target.value)}
                inputProps={{ "data-testid": "perfil-otro-giro-input", maxLength: 120 }}
              />
            </FieldWrapper>
          </Box>
        )}

        {tieneAgri && (
          <>
            <Divider sx={{ my: 2 }} />
            <QueSiembras value={siembras} onChange={setSiembras} />
          </>
        )}

        {tieneGan && (
          <>
            <Divider sx={{ my: 2 }} />
            <QueGanado value={ganado} onChange={setGanado} n={tieneAgri ? 3 : 2} />
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <ConQuienOperas value={conQuien} onChange={setConQuien} n={nConQuien} />

        <Divider sx={{ my: 2 }} />
        <ConQueOperas
          value={conQue}
          onChange={setConQue}
          hectareasRentadas={hectareasRentadas}
          onHectareasChange={setHectareasRentadas}
          n={nConQue}
        />
      </Paper>

      <StepActions
        onBack={() => {
          if (previousStatus) navigate(previousStatus.url);
        }}
        onNext={handleNext}
        nextLabel="Continuar"
        nextDisabled={!canContinue || isSubmitting}
        backTestId="perfil-operativo-back-button"
        nextTestId="perfil-operativo-continue-button"
      />
    </Box>
  );
};
